import { useTRPC } from "@/trpc/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [["intelligence"]] });
}

/**
 * Deliberately does not invalidate, unlike the other three. `createUpload` only
 * reserves the record — the bytes are not in Storage yet, and the dialog rolls
 * the record back if the upload fails. Showing it in the list here would flash
 * a pending row that may be deleted seconds later; the dialog owns the UX until
 * `useFinalizeDocumentUpload`, which is where the document becomes real.
 */
export function useCreateDocumentUpload() {
  const trpc = useTRPC();
  return useMutation(trpc.intelligence.documents.createUpload.mutationOptions());
}

export function useFinalizeDocumentUpload() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  return useMutation({
    ...trpc.intelligence.documents.finalizeUpload.mutationOptions(),
    onSettled: () => invalidate(qc),
  });
}

/** Re-runs indexing for one document — the retry on a failed or stalled row. */
export function useReindexDocument() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  return useMutation({
    ...trpc.intelligence.documents.reindex.mutationOptions(),
    onSettled: () => invalidate(qc),
  });
}

/**
 * Fetches a time-limited link to the original file.
 *
 * A mutation rather than a query on purpose: the URL expires, and a cached
 * query would hand a long-open tab a dead link.
 */
export function useDocumentDownloadUrl() {
  const trpc = useTRPC();
  return useMutation(
    trpc.intelligence.documents.getDownloadUrl.mutationOptions(),
  );
}

export function useUpdateDocument() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  return useMutation({
    ...trpc.intelligence.documents.update.mutationOptions(),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteDocument() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  return useMutation({
    ...trpc.intelligence.documents.delete.mutationOptions(),
    onSuccess: () => invalidate(qc),
  });
}
